import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Card, CardHeader, CardContent, Dialog, Checkbox } from '@material-ui/core';
import { colors, TextInput, Button, Typography, Loader } from 'kangarootime-ui';
import { DayPickerSingleDateController } from 'react-dates';
import RichTextEditor from '../../../components/RichTextEditor';
import moment from 'moment';
import './EventPopup.css';

const EventPopup = ({ isOpen, onClose, event, onSave, newEventStartDate, creatingEvent, newEventEndDate }) => {
  const CREATING_EVENT = event === null;
  const title = CREATING_EVENT ? 'Add Event' : 'Edit Event';
  // state
  const [eventTitle, setEventTitle] = useState(CREATING_EVENT ? '' : event.title);
  const [eventLocation, setEventLocation] = useState(CREATING_EVENT ? '' : event.location);
  const [eventDate, setEventDate] = useState(CREATING_EVENT ? newEventStartDate : moment(event.start));
  const [eventAllDay, setAllDay] = useState(CREATING_EVENT ? false : Boolean(event.allDay));
  const [eventStartTime, setEventStartTime] = useState(CREATING_EVENT ? newEventStartDate : moment(event.starts_at));
  const [eventEndTime, setEventEndTime] = useState(CREATING_EVENT ? newEventEndDate : moment(event.ends_at));
  const [eventDescription, setEventDescription] = useState(CREATING_EVENT ? '' : event.description);

  useEffect(() => {
    // if `eventDate` is a different than the date of `eventStartTime`, updates `eventStartTime`'s date
    if (!eventDate.isSame(eventStartTime, 'day')) {
      const newMonth = eventDate.month();
      const newDay = eventDate.date();
      const newYear = eventDate.year();
      const updatedStartTimeDate = eventStartTime.clone().month(newMonth).date(newDay).year(newYear);
      const updatedEndTimeDate = eventEndTime.clone().month(newMonth).date(newDay).year(newYear);

      setEventStartTime(updatedStartTimeDate);
      setEventEndTime(updatedEndTimeDate);
    }
  }, [eventDate]);

  const handleTimeInput = (event, type) => {
    const time = event.target.value; // 2pm would be 14:00 so we can split on : to get the inputted hours and minutes
    const [hours, minutes] = time.split(':');
    const timeToMoment = eventDate.clone().hours(hours).minutes(minutes);

    type === 'start' ? setEventStartTime(timeToMoment) : setEventEndTime(timeToMoment);
  };

  const saveEvent = () => {
    const id = CREATING_EVENT ? null : event.id;

    // need prop action
    onSave(id, eventTitle, eventLocation, eventDate.toISOString(), eventStartTime.toISOString(), eventEndTime.toISOString(), eventDescription, eventAllDay);
  };

  const isValid = () => {
    return (
      eventTitle.trim().length > 0 &&
      eventDate.isSameOrAfter(moment(), 'day') &&
      eventDescription.trim().length > 0 &&
      (!eventAllDay ? moment(eventStartTime).isBefore(moment(eventEndTime)) : true) &&
      eventDescription !== '<p></p>' // need this check since the default value for <RichTextEditor /> is an empty paragraph group
    );
  };

  return (
    <Dialog open={isOpen} onClose={onClose} disableEnforceFocus={false}>
      <Card>
        <CardHeader style={{ backgroundColor: colors.brand_1, color: '#FFFFFF' }} title={title} />
        <CardContent>
          <div className='event-popup-container'>
            <div className='event-popup-inputs'>
              <TextInput
                label='Title'
                value={eventTitle}
                hasError={eventTitle === ''}
                errorText='A title is required'
                onChange={event => setEventTitle(event.target.value)}
                style={{ width: '100%' }}
              />
              <div style={{ marginTop: 8 }}>
                <TextInput
                  label='Location'
                  value={eventLocation}
                  onChange={event => setEventLocation(event.target.value)}
                  style={{ width: '100%' }}
                />
              </div>
              <div className='flex-rows' style={{ alignItems: 'center', marginTop: 8 }}>
                <Checkbox
                  checked={eventAllDay}
                  onChange={() => setAllDay(!eventAllDay)}
                  style={{ color: colors.brand_3, padding: 0, marginRight: 8 }}
                />
                <Typography style={{ fontSize: '0.875rem', fontWeight: 600 }}>All Day event?</Typography>
              </div>
              { !eventAllDay &&
                <>
                  <div style={{ marginTop: 8 }}>
                    <TextInput
                      label='Start Time'
                      type='time'
                      value={eventStartTime && eventStartTime.format('HH:mm')}
                      onChange={event => handleTimeInput(event, 'start')}
                      inputProps={{ step: 300 }}
                      required
                      style={{ width: '100%' }}
                      hasError={moment(eventStartTime).isAfter(moment(eventEndTime))}
                      errorText='Start time must come before the end time'
                    />
                  </div>
                  <div style={{ marginTop: 8 }}>
                    <TextInput
                      label='End Time'
                      type='time'
                      value={eventEndTime && eventEndTime.format('HH:mm')}
                      onChange={event => handleTimeInput(event, 'end')}
                      inputProps={{ step: 300 }}
                      required
                      style={{ width: '100%' }}
                      hasError={moment(eventEndTime).isBefore(moment(eventStartTime))}
                      errorText='End time must come after the start time'
                    />
                  </div>
                </>
              }
            </div>
            <div className='event-popup-calendar'>
              <DayPickerSingleDateController
                hideKeyboardShortcutsPanel
                numberOfMonths={1}
                date={eventDate}
                onDateChange={day => setEventDate(day)}
              />
            </div>
          </div>
          <div style={{ marginTop: 8 }}>
            <Typography style={{ fontSize: '0.875rem', fontWeight: 600 }}>Description</Typography>
            <RichTextEditor
              value={eventDescription}
              onTextChange={text => setEventDescription(text)}
            />
          </div>
          <Loader loading={creatingEvent}>
            <div className='flex-columns' style={{ marginTop: 16, alignItems: 'center' }}>
              <Button onClick={saveEvent} disabled={!isValid()}>Save</Button>
              <div onClick={onClose} style={{ marginTop: 8, cursor: 'pointer' }}>
                <Typography style={{ color: '#6B778C' }}>Cancel</Typography>
              </div>
            </div>
          </Loader>
        </CardContent>
      </Card>
    </Dialog>
  );
};

EventPopup.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  event: PropTypes.object, // if null then a new event is being created
  onSave: PropTypes.func,
  newEventStartDate: PropTypes.object, // moment object
  creatingEvent: PropTypes.bool,
  newEventEndDate: PropTypes.object // moment object
};

export default EventPopup;
